/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart as RechartsLineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency, formatToKMB } from '../../../../utils';

const CustomSlider = styled(Slider)<{ thumbcolor?: string; zeroliney?: number }>(({ theme, thumbcolor, zeroliney }) => ({
  padding: '0px !important',
  top: zeroliney ? `calc(${zeroliney}px - 40px)` : 'auto',
  bottom: 'auto',
  left: 60,
  right: 51,
  width: 'auto',
  position: 'absolute',
  transform: 'translateY(-50%)',
  [theme.breakpoints.up('md')]: {
    right: 20,
  },
  '& .MuiSlider-rail': {
    display: 'none'
  },
  '& .MuiSlider-mark': {
    display: 'none'
  },
  '& .MuiSlider-thumb': {
    border: 2,
    width: 20,
    height: 20,
    borderStyle: 'solid',
    background: thumbcolor,
    borderColor: theme.palette.common.white,
    transform: 'translate(-50%)',
    transformOrigin: '50% 50%',
    '&::before': {
      boxShadow: 'none',
    },
    '&:hover, &:focus': {
      background: thumbcolor,
      transition: theme.transitions.create(['scale'], {
        duration: theme.transitions.duration.short,
        easing: theme.transitions.easing.easeInOut,
      }),
    }
  }
}));

const CustomTooltip = ({ active, payload, label, lines, tooltipLabel }: any) => {
  const { t } = useTranslation('overview');
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: theme.palette.navy[200],
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          paddingRight: 4,
        }}
      >
        <Typography gutterBottom variant="h4" mb={2}>
          {tooltipLabel ? tooltipLabel(label) : dayjs(label).format(t('format.tooltipDate', { ns: 'overview' }))}
        </Typography>
        {payload.map((entry: any, index: number) => {
          const getLineStyle = (dataKey: string) => {
            switch (dataKey) {
              case 'value1': // Current period - solid line
                return {
                  width: 20,
                  height: 0,
                  borderRadius: 1,
                  borderBottom: `${entry.strokeWidth || 1}px solid ${entry.color}`,
                };
              case 'value2': // Previous period - dotted line
                return {
                  width: 20,
                  height: 0,
                  borderRadius: 1,
                  borderBottom: `${entry.strokeWidth || 1}px solid ${entry.color}`,
                };
              case 'value3': // Pre-previous period - dashed line
                return {
                  width: 20,
                  height: 0,
                  borderRadius: 1,
                  borderBottom: `${entry.strokeWidth || 1}px solid ${entry.color}`,
                };
              default:
                return {
                  width: 20,
                  height: 0,
                  borderRadius: 1,
                  borderBottom: `${entry.strokeWidth || 1}px solid ${entry.color}`,
                };
            }
          };

          return (
            <Box key={index} display="flex" alignItems="center" gap={1} mb={0.5}>
              <Box sx={getLineStyle(entry.dataKey)} />
              <Typography>{formatCurrency(entry.value)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }
  return null;
};

export interface LineData {
  time: number | string | Date;
  [key: string]: number | string | Date | null;
}

export interface LineConfig {
  dataKey: string;
  color: string;
  label: string;
  name?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
}

export interface YAxisProps {
  ticks?: number[] | string[];
  axisLine?: boolean;
  tickLine?: boolean;
  domain?: [number, number];
  tickFormatter?: (value: any) => string;
  stroke?: string;
  fontSize?: number;
  [key: string]: any; // Allow any other YAxis props
}

export interface XAxisProps {
  axisLine?: boolean;
  dataKey?: string;
  tickMargin?: number;
  tickLine?: boolean;
  ticks?: number[] | string[];
  interval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
  domain?: [number, number];
  tickFormatter?: (value: any) => string;
  [key: string]: any; // Allow any other XAxis props
}

export interface LineChartProps {
  data: LineData[];
  lines: LineConfig[];
  showSlider?: boolean;
  showGrid?: boolean;
  showReferenceLine?: boolean;
  dateFormat?: string;
  tickCount?: number;
  onDataPointSelect?: (item: LineData) => void;
  onSliderChange?: (event: Event, newValue: number | number[]) => void;
  formatValue?: (value: number) => string;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  thumbColor?: string;
  width?: number | '100%';
  height?: number;
  yAxisProps?: YAxisProps;
  xAxisProps?: XAxisProps;
  tooltipLabel?: (time: any) => string;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  lines,
  showSlider = false,
  showGrid = true,
  showReferenceLine = false,
  dateFormat = 'DD',
  tickCount = 5,
  onDataPointSelect,
  margin = { top: 20, right: 0, bottom: 0, left: 0 },
  thumbColor = '#EDEAF6',
  width,
  height,
  yAxisProps = {},
  xAxisProps = {},
  tooltipLabel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('overview');
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const useResponsive = width === '100%';
  const chartWidth = useResponsive ? undefined : (typeof width === 'number' ? width : (isDesktop ? 552 : 362));
  const chartHeight = height || (isDesktop ? 400 : 360);
  const [referenceLineValue, setReferenceLineValue] = useState<number>(0);
  const [minTime, setMinTime] = useState<number>(0);
  const [maxTime, setMaxTime] = useState<number>(0);
  const [minAmount, setMinAmount] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<number>(0);
  const [ticks, setTicks] = useState<number[]>([]);
  const [zeroLineY, setZeroLineY] = useState<number>(0);
  const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>(
    lines.reduce((acc, line) => ({ ...acc, [line.dataKey]: true }), {})
  );

  // Initialize chart data and ranges
  useEffect(() => {
    if (!data.length) return;

    const activeLines = lines.filter(line => visibleLines[line.dataKey]);
    if (!activeLines.length) return;

    const timeValues = data.map(d => dayjs(d.time).valueOf());
    const minT = Math.min(...timeValues);
    const maxT = Math.max(...timeValues);
    setMinTime(minT);
    setMaxTime(maxT);

    // Always set the reference line and selected item to the first time value (x=0)
    const firstTimeValue = minT;
    setReferenceLineValue(firstTimeValue);

    // Calculate Y-axis range from all line values
    const allValues: number[] = [];
    data.forEach(item => {
      activeLines.forEach(line => {
        const value = item[line.dataKey] as number | null;
        if (value === null || value === 0) return;
        if (typeof value === 'number' && !isNaN(value)) {
          allValues.push(value);
        }
      });
    });

    if (allValues.length > 0) {
      const min = 0;
      const max = Math.max(...allValues);
      const padding = max * 0.1;
      const adjustedMin = min;
      const adjustedMax = max + padding;

      setMinAmount(adjustedMin);
      setMaxAmount(adjustedMax);

      const marginBottom = margin.bottom || 0;
      const zeroPositionFromTop = chartHeight - marginBottom;
      setZeroLineY(zeroPositionFromTop);

      const step = (adjustedMax - adjustedMin) / (tickCount - 1);
      const tickValues = Array.from({ length: tickCount }, (_, i) =>
        adjustedMin + (step * i)
      );
      setTicks(tickValues);
    } else {
      const marginBottom = margin.bottom || 0;
      setZeroLineY(chartHeight - marginBottom);
    }
  }, [data, lines, tickCount, chartHeight, margin, visibleLines]);

  const handleSliderChange = useCallback((event: Event, newValue: number | number[]) => {
    const tolerance = 86400000; // 1 day in milliseconds
    const valueAsNumber = Array.isArray(newValue) ? newValue[0] : newValue;

    const item = data.find(datum => {
      const itemTime = dayjs(datum.time).valueOf();
      return Math.abs(itemTime - valueAsNumber) <= tolerance;
    });

    if (item) {
      onDataPointSelect?.(item);
    }
    setReferenceLineValue(valueAsNumber);
  }, [data, onDataPointSelect]);

  const handleChartInteraction = useCallback((e: any) => {
    const value = e?.activeLabel as number;
    if (value) {
      handleSliderChange(e, value);
    }
  }, [handleSliderChange]);

  const xAxisTicks = React.useMemo(() => {
    if (xAxisProps.ticks) return xAxisProps.ticks;

    if (!data.length) return [];

    // Extract actual time values from data
    const timeValues = data.map(d => dayjs(d.time).valueOf()).sort((a, b) => a - b);

    // If we have fewer data points than tickCount, return all time values
    if (timeValues.length <= tickCount) {
      return timeValues;
    }

    // Otherwise, select evenly distributed time values from the actual data
    const step = Math.floor(timeValues.length / (tickCount - 1));
    const selectedTicks = [];

    for (let i = 0; i < tickCount - 1; i++) {
      selectedTicks.push(timeValues[i * step]);
    }
    // Always include the last time value
    selectedTicks.push(timeValues[timeValues.length - 1]);

    return selectedTicks;
  }, [data, xAxisProps.ticks]);

  const handleLegendClick = useCallback((dataKey: string) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  }, []);

  const chartContent = (
    <RechartsLineChart
      data={data}
      margin={margin}
      width={useResponsive ? undefined : chartWidth}
      height={chartHeight}
      onClick={handleChartInteraction}
      onMouseMove={handleChartInteraction}
      onMouseLeave={handleChartInteraction}
    >
      {lines.map((line, index) => (
        <Line
          key={index}
          dot={false}
          type="monotone"
          stroke={line.color}
          dataKey={line.dataKey}
          strokeWidth={line.strokeWidth || 2}
          name={line.name || (index === 0 ? t('time.thisMonth') : index === 1 ? t('time.lastMonth') : t('time.twoMonthsAgo'))}
          activeDot={{ r: 4, fill: line.color }}
          strokeDasharray={line.strokeDasharray}
          hide={!visibleLines[line.dataKey]}
        />
      ))}

      <Legend
        iconSize={0}
        align="right"
        verticalAlign="top"
        wrapperStyle={{
          left: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          fontSize: 12,
          marginTop: -20,
          paddingInline: theme.spacing(2),
        }}
        formatter={(value, entry: any) => {
          return (
            <span
              style={{
                color: visibleLines[entry.dataKey] ? theme.palette.text.secondary : theme.palette.text.disabled,
                gap: '4px',
                display: 'flex',
                cursor: 'pointer',
                alignItems: 'center',
              }}
              onClick={() => handleLegendClick(entry.dataKey)}
            >
              <Box
                sx={{
                  height: 0,
                  width: 20,
                  borderRadius: 1,
                  borderBottom: `${entry.strokeWidth || 1}px solid ${entry.color}`,
                  borderColor: visibleLines[entry.dataKey] ? entry.color : theme.palette.text.disabled,
                }}
              />
              <Typography variant="body2" fontWeight={600}>
                {value}
              </Typography>
            </span>
          );
        }}
        payload={lines.map(line => ({
          type: 'line',
          value: line.label,
          color: line.color,
          dataKey: line.dataKey,
          strokeWidth: line.strokeWidth || 1
        }))}
      />

      {/* Grid */}
      {showGrid && (
        <CartesianGrid
          vertical={false}
          strokeDasharray="5 5"
        />
      )}

      {/* Y Axis */}
      <YAxis
        ticks={ticks}
        axisLine={false}
        tickLine={false}
        domain={[minAmount, maxAmount]}
        tickFormatter={(value) => formatToKMB(value, true)}
        stroke={theme.palette.text.secondary}
        fontSize={12}
        {...yAxisProps}
      />

      {/* X Axis */}
      <XAxis
        {...xAxisProps}
        interval={xAxisProps.interval !== undefined ? xAxisProps.interval : 0}
        axisLine={xAxisProps.axisLine !== undefined ? xAxisProps.axisLine : true}
        dataKey={xAxisProps.dataKey || "time"}
        tickMargin={xAxisProps.tickMargin || 16}
        tickLine={xAxisProps.tickLine !== undefined ? xAxisProps.tickLine : false}
        ticks={xAxisTicks}
        domain={xAxisProps.domain || [minTime, maxTime]}
        tickFormatter={xAxisProps.tickFormatter || ((timeStr) => {
          if (typeof timeStr === 'string') {
            return timeStr;
          }

          return dayjs(timeStr).format(dateFormat);
        })}
      />

      {showReferenceLine && (
        <ReferenceLine
          x={referenceLineValue}
          stroke={theme.palette.text.secondary}
          strokeWidth={1}
        />
      )}

      {/* Tooltip */}
      <Tooltip
        content={<CustomTooltip lines={lines} tooltipLabel={tooltipLabel} />}
        cursor={{ stroke: theme.palette.divider, strokeWidth: 1 }}
      />
    </RechartsLineChart>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: useResponsive ? '100%' : 'auto',
      }}
    >
      {useResponsive ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          {chartContent}
        </ResponsiveContainer>
      ) : (
        chartContent
      )}

      {/* Interactive Slider */}
      {false && showSlider && referenceLineValue && (
        <CustomSlider
          track={false}
          value={referenceLineValue}
          min={minTime}
          max={maxTime}
          step={86400000}
          onChange={handleSliderChange}
          valueLabelDisplay="off"
          aria-labelledby="line-chart-slider"
          thumbcolor={thumbColor}
          zeroliney={zeroLineY}
        />
      )}
    </Box>
  );
};

export default LineChart;
